import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Cancel,
  ArrowBack,
  Email,
  Chat,
  Help,
  Refresh,
} from '@mui/icons-material';

export const StudioSignupCancel: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0E1A',
        backgroundImage: `
          radial-gradient(ellipse at top right, rgba(255, 193, 7, 0.1) 0%, transparent 50%),
          radial-gradient(ellipse at bottom left, rgba(255, 107, 53, 0.1) 0%, transparent 50%)
        `,
        padding: 2,
      }}
    >
      <Card sx={{ 
        maxWidth: 600, 
        width: '100%',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 3,
              padding: 3,
              borderRadius: '50%',
              background: 'rgba(255, 193, 7, 0.1)',
              border: '2px solid rgba(255, 193, 7, 0.3)',
            }}>
              <Cancel sx={{ fontSize: 60, color: 'warning.main' }} />
            </Box>
            
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Signup Cancelled
            </Typography>
            
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your studio signup was not completed
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Don't worry! Your information has not been saved and no charges have been made. 
              You can restart the signup process at any time.
            </Typography>
          </Alert>

          <Card sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom>
              Need help?
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Help color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Questions about plans?"
                  secondary="Compare our Basic, Professional, and Enterprise options"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Email color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Contact sales"
                  secondary="Email us at sales@abydosone.com for assistance"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Chat color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Live chat support"
                  secondary="Available Monday-Friday, 9am-5pm EST"
                />
              </ListItem>
            </List>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/studio-signup')}
              startIcon={<Refresh />}
              sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 24px rgba(255, 107, 53, 0.4)',
                },
              }}
            >
              Try Again
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/')}
              startIcon={<ArrowBack />}
            >
              Back to Home
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/login')}
                sx={{ color: 'primary.main' }}
              >
                Sign in
              </Button>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};